import { Card, CardContent } from "@/components/ui/card";
import { Quote, ExternalLink, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback } from 'react';

const Testimonials = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    align: 'start',
    skipSnaps: false,
    breakpoints: {
      '(min-width: 768px)': { slidesToScroll: 2 },
      '(min-width: 1024px)': { slidesToScroll: 3 }
    }
  });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi]);

  const testimonials = [
    {
      name: "N. R.",
      date: "Aug 2025",
      text: "Dr Luis spoke perfect English and was very easy to talk to. He offered great advice for our travel plans and it was super quick to get the appointment and prescription. Highly recommend for travellers."
    },
    {
      name: "S. L.",
      date: "Jun 2025",
      text: "I was very thankful to find a telehealth doctor who didn't require a Brazilian CPF. His English was excellent and he was a great listener."
    },
    {
      name: "D. A.",
      date: "Jun 2025",
      text: "10 out of 10! I'm an elderly American visiting Brazil for the first time. Dr. Luis saw me within 30 minutes, was kind, patient, and spoke great English. I didn't have to leave my hotel, and within five minutes after our call I had my prescription. Highly recommend!"
    },
    {
      name: "I. V.",
      date: "Jun 2025",
      text: "Dr. Luis answered all my questions, explained the treatment in detail, and was always patient and professional. I felt very confident and well taken care of."
    },
    {
      name: "E. O.",
      date: "Feb 2025",
      text: "Luis was thorough and approachable. He made sure he had the whole picture before prescribing medication, which put my mind at ease while traveling overseas."
    },
    {
      name: "S. A.",
      date: "Feb 2025",
      text: "Dr. Santos was absolutely excellent. His English was terrific and his thoroughness really put me at ease when I was very ill."
    }
  ];

  return (
    <section id="testimonials" className="py-20 bg-background" itemScope itemType="https://schema.org/Organization">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-foreground mb-4">
            What Patients Say
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
             Our patients' words speak louder than ours. Here's what travelers, expats, and locals say about their experience with Rio Med Care.
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative mb-12">
          {/* Navigation Buttons */}
          <div className="flex justify-center md:justify-end mb-6 space-x-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={scrollPrev}
              className="hover-scale"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={scrollNext}
              className="hover-scale"
              aria-label="Next testimonial"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Carousel */}
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {testimonials.map((testimonial, index) => (
                <div 
                  key={index} 
                  className="flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0 pl-4"
                >
                  <Card 
                    className="h-full hover:shadow-lg transition-all duration-300 bg-white hover-scale mr-4"
                    itemScope 
                    itemType="https://schema.org/Review"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-0.5" aria-label="Rated 5 out of 5">
                          {[0, 1, 2, 3, 4].map((i) => (
                            <Star key={i} className="h-4 w-4 fill-accent text-accent" />
                          ))}
                        </div>
                        <Quote className="h-7 w-7 text-primary opacity-40" />
                      </div>

                      <p className="text-foreground leading-relaxed mb-6 line-clamp-4" itemProp="reviewBody">
                        "{testimonial.text}"
                      </p>

                      <div className="flex items-center gap-3 pt-4 border-t border-border">
                        <div className="h-11 w-11 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm">
                          {testimonial.name.replace(/[^A-Za-z]/g, "")}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground" itemProp="author">{testimonial.name}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                            <span itemProp="datePublished">{testimonial.date}</span>
                            <span>•</span>
                            <span className="bg-primary/10 text-primary px-2 py-0.5 rounded text-xs font-medium">
                              Verified Patient
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Hidden schema.org data */}
                      <div itemScope itemType="https://schema.org/Rating" itemProp="reviewRating" style={{display: 'none'}}>
                        <span itemProp="ratingValue">5</span>
                        <span itemProp="bestRating">5</span>
                      </div>
                      <div itemProp="itemReviewed" itemScope itemType="https://schema.org/MedicalBusiness" style={{display: 'none'}}>
                         <span itemProp="name">Rio Med Care</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="text-center">
          <Button 
            variant="outline" 
            className="group"
            onClick={() => window.open('https://jitnikovdossantos.doctorsa.com/', '_blank')}
          >
            See all reviews
            <ExternalLink className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;